<template>
    <div class="mx-auto flex max-w-screen-xl items-start gap-8">
        <div class="w-full">
            <div
                v-if="useGroupedLayout"
                class="mt-8 grid gap-8"
                :class="columnClass"
            >
                <div
                    v-for="(column, columnIndex) in groupColumns"
                    :key="`column-${columnIndex}`"
                    class="space-y-8"
                >
                    <div
                        v-for="group in column"
                        :key="group.id"
                        class="space-y-4"
                    >
                        <div
                            class="text-base font-semibold uppercase tracking-wide text-gray-800"
                        >
                            <span class="inline-flex items-center gap-2">
                                <span
                                    class="h-2 w-2 rounded-full bg-primary-600"
                                />
                                {{ group.name }}
                            </span>
                        </div>
                        <div class="grid gap-4">
                            <nuxt-link
                                v-for="facility in group.facilities"
                                :key="facility.label"
                                :to="facility.route"
                            >
                                <prime-button
                                    class="w-full whitespace-nowrap px-16 py-4 !text-3xl"
                                    :label="facility.label"
                                />
                            </nuxt-link>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="mt-8 grid grid-cols-[1fr,min-content,1fr] gap-8"
            >
                <nuxt-link
                    v-for="facility in facilities"
                    :key="facility.label"
                    :to="facility.route"
                    class="col-start-2"
                >
                    <prime-button
                        class="w-full whitespace-nowrap px-16 py-4 !text-3xl"
                        :label="facility.label"
                    />
                </nuxt-link>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { type ActiveFacility, provideActiveFacility, useViewer } from '#imports'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import NuxtLink from '#app/components/nuxt-link'
import { useHead } from '@unhead/vue'
import { useRouteLink } from '~/composables/routing'
import { useRpQuery } from '~/composables/graphql'
import { provideViewer } from '~/composables/use-login'
import { useRequestURL } from '#app'

type FacilityLink = {
    id: string
    label: string
    route: string
}

type FacilityGroupDisplay = {
    id: string
    name: string
    facilities: FacilityLink[]
}

const url = useRequestURL()
const query = useRpQuery({ orgLevel: true })
const { data, suspense } = useQuery({
    queryKey: ['LandingPageQuery', url.hostname],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query LandingPageQuery {
                    organization {
                        code
                        shortName
                        longName
                        logo
                        branding {
                            styleString
                        }
                        facilityLocationGroups {
                            id
                            name
                            facilities {
                                id
                                label
                                slug
                            }
                        }
                    }
                }
            `)
        )
})

const { facilityHomeLink } = useRouteLink()
const groupedFacilities = computed<FacilityGroupDisplay[]>(() => {
    return (
        data.value?.organization?.facilityLocationGroups.map(group => ({
            id: group.id,
            name: group.name,
            facilities: group.facilities.map(facility => ({
                id: facility.id,
                label: facility.label,
                route: facilityHomeLink(facility.slug.toLowerCase())
            }))
        })) ?? []
    )
})

const facilities = computed(() => {
    return groupedFacilities.value.flatMap(group => group.facilities)
})

const useGroupedLayout = computed(() => groupedFacilities.value.length > 1)

const groupColumns = computed<FacilityGroupDisplay[][]>(() => {
    const groups = groupedFacilities.value
    if (groups.length === 0) {
        return []
    }

    const sizes = groups.map(group => 1 + group.facilities.length)
    const prefix = sizes.reduce<number[]>((acc, size) => {
        acc.push((acc.at(-1) ?? 0) + size)
        return acc
    }, [])

    const sumRange = (start: number, end: number) =>
        prefix[end - 1] - (start > 0 ? prefix[start - 1] : 0)

    type Partition = {
        columnCount: number
        splits: number[]
        maxSum: number
        variance: number
    }

    const partitions: Partition[] = []

    const total = prefix[prefix.length - 1]

    const recordPartition = (columnCount: number, splits: number[]) => {
        const boundaries = [0, ...splits, groups.length]
        const sums = boundaries
            .slice(1)
            .map((end, index) => sumRange(boundaries[index], end))
        const maxSum = Math.max(...sums)
        const mean = total / columnCount
        const variance =
            sums.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) /
            columnCount
        partitions.push({ columnCount, splits, maxSum, variance })
    }

    recordPartition(1, [])

    if (groups.length >= 2) {
        for (let i = 1; i < groups.length; i += 1) {
            recordPartition(2, [i])
        }
    }

    if (groups.length >= 3) {
        for (let i = 1; i < groups.length - 1; i += 1) {
            for (let j = i + 1; j < groups.length; j += 1) {
                recordPartition(3, [i, j])
            }
        }
    }

    if (groups.length >= 4) {
        for (let i = 1; i < groups.length - 2; i += 1) {
            for (let j = i + 1; j < groups.length - 1; j += 1) {
                for (let k = j + 1; k < groups.length; k += 1) {
                    recordPartition(4, [i, j, k])
                }
            }
        }
    }

    const best = partitions.sort((a, b) => {
        if (a.maxSum !== b.maxSum) {
            return a.maxSum - b.maxSum
        }
        if (a.variance !== b.variance) {
            return a.variance - b.variance
        }
        return a.columnCount - b.columnCount
    })[0]

    const boundaries = [0, ...best.splits, groups.length]
    return boundaries.slice(1).map((end, index) => {
        return groups.slice(boundaries[index], end)
    })
})

const columnClass = computed(() => {
    const count = groupColumns.value.length
    if (count >= 4) {
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    }
    if (count === 3) {
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    }
    if (count === 2) {
        return 'grid-cols-1 md:grid-cols-2'
    }
    return 'grid-cols-1'
})

const organization = computed(() => data.value?.organization)

await Promise.all([suspense(), provideViewer()])
const viewer = useViewer()
if (viewer.value !== undefined) {
    const homeFacility = ref<ActiveFacility>(viewer.value.homeFacility)
    provideActiveFacility(homeFacility)
}

useHead({
    title: organization.value?.longName,
    style: () => {
        return [
            {
                innerHTML: organization.value?.branding?.styleString ?? ''
            }
        ]
    }
})
</script>
